/** Session key for preserving deep-link path across MApp OAuth round-trip. */
export const PULSE_RETURN_URL_KEY = 'pulse_return_url';

const AUTH_QUERY_PARAMS = ['mappToken', 'environment', 'brand'] as const;

/** Respondent routes that should survive the OAuth round-trip. */
export function isRespondentDeepLink(
  pathname = window.location.pathname,
  search = window.location.search,
): boolean {
  if (/\/forms\/[^/]+\/submit/.test(pathname)) return true;
  const params = new URLSearchParams(search);
  return /\/forms\/[^/]+$/.test(pathname) && (params.has('linkId') || params.has('link'));
}

/** Persist the current path + query before redirecting to MApp login. */
export function preserveReturnUrl(): void {
  const returnUrl = `${window.location.pathname}${window.location.search}`;
  sessionStorage.setItem(PULSE_RETURN_URL_KEY, returnUrl);
}

/**
 * Save the current respondent deep link on first visit (before OAuth strips the URL).
 * Skips OAuth callback loads that already carry `mappToken`.
 */
export function preserveReturnUrlForDeepLink(hasAuthCallback = false): void {
  if (hasAuthCallback || !isRespondentDeepLink()) return;
  preserveReturnUrl();
}

/**
 * After auth completes, restore the saved deep link when OAuth landed on a generic
 * entry URL (e.g. `/forms`) instead of the original submit path.
 *
 * Uses `location.replace` so React Router remounts on the correct route — `replaceState`
 * alone does not update the router's matched route.
 *
 * @returns true when a redirect was initiated (caller should stop further UI updates)
 */
export function restoreReturnUrlAfterAuth(): boolean {
  const saved = sessionStorage.getItem(PULSE_RETURN_URL_KEY);
  if (!saved) return false;

  sessionStorage.removeItem(PULSE_RETURN_URL_KEY);
  const current = stripAuthParamsFromUrl();
  if (saved === current) return false;

  window.location.replace(saved);
  return true;
}

/**
 * Resolve the respondent link token from the current URL, falling back to the
 * OAuth return URL saved in sessionStorage when auth callback strips query params.
 */
export function resolveRespondentLinkId(
  searchParams: URLSearchParams = new URLSearchParams(window.location.search),
): string | undefined {
  const fromUrl = searchParams.get('linkId') || searchParams.get('link');
  if (fromUrl) return fromUrl;

  const saved = sessionStorage.getItem(PULSE_RETURN_URL_KEY);
  if (!saved) return undefined;

  const queryStart = saved.indexOf('?');
  if (queryStart === -1) return undefined;

  const savedParams = new URLSearchParams(saved.slice(queryStart + 1));
  return savedParams.get('linkId') || savedParams.get('link') || undefined;
}

/** Remove OAuth callback params while keeping respondent deep-link query params. */
export function stripAuthParamsFromUrl(pathname = window.location.pathname, search = window.location.search): string {
  const params = new URLSearchParams(search);
  for (const key of AUTH_QUERY_PARAMS) {
    params.delete(key);
  }
  const qs = params.toString();
  return `${pathname}${qs ? `?${qs}` : ''}`;
}
